import { FailureRemediationResponse } from '@/api/generatedApi';
import { FailureKind } from '@/hooks/api/assets/useGetRecentFailures';
import { RemediableFailureKind } from '@/hooks/api/failures/remediableFailureKind';
import { pluralize } from '@/utils/format';

export interface RemediationCopy {
  verb: string;
  verbImperative: string;
  itemSingular: string;
  itemPlural: string;
}

export const REMEDIATION_COPY: Record<RemediableFailureKind, RemediationCopy> = {
  asset: { verb: 'reprocess', verbImperative: 'Reprocess', itemSingular: 'asset', itemPlural: 'assets' },
  'part-finder-report': { verb: 'retry', verbImperative: 'Retry', itemSingular: 'search', itemPlural: 'searches' },
};

export const isRemediableKind = (kind: FailureKind): kind is RemediableFailureKind => kind in REMEDIATION_COPY;

const RETRY_OUTCOME_HINT = 'Anything that fails again shows back up here, marked as retried.';

export interface RemediationOutcomeMessage {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

const describeCount = (count: number, copy: RemediationCopy) => pluralize(count, copy.itemSingular, copy.itemPlural);

const asSentence = (text: string) => (text.endsWith('.') ? text : `${text}.`);

const describeSkipped = (result: FailureRemediationResponse) => {
  const [only] = result.skipped;
  if (result.skipped.length === 1 && only)
    return `${only.name} was not queued: ${only.reason ?? 'the file is the problem'}`;
  return `${result.skipped.length} were not queued because the file is the problem`;
};

export const describeRemediationOutcome = (
  result: FailureRemediationResponse,
  copy: RemediationCopy
): RemediationOutcomeMessage => {
  const queued =
    result.queued.length > 0
      ? `Queued ${describeCount(result.queued.length, copy)} to ${copy.verb}. ${RETRY_OUTCOME_HINT}`
      : '';
  if (result.failed.length > 0) {
    const [first] = result.failed;
    return {
      type: 'error',
      message: `${queued} Could not ${copy.verb} ${describeCount(result.failed.length, copy)}: ${first.message}`.trim(),
    };
  }
  if (result.skipped.length > 0) {
    return { type: queued ? 'success' : 'warning', message: `${queued} ${asSentence(describeSkipped(result))}`.trim() };
  }
  if (queued) return { type: 'success', message: queued };
  return { type: 'info', message: `Nothing to ${copy.verb}. It may have been fixed already.` };
};
